import * as palette from "@govlaunch/palette";
import React, { ReactElement, createRef } from "react";
import { Margin } from "~/components/spacings";
import { Value } from "react-powerplug";
import { useIntl } from "react-intl";

interface IHighlightsInputProps {
  value: any;
  maxItems: number;
  characterLimit: number;
  onChange: (highlights: any) => void;
  bulletColor?: string;
  inputBorderColor?: string;
  [key: string]: any;
}

interface IHighlightBullet {
  color?: string;
}

export default function HighlightsInput({
  value,
  onChange,
  maxItems,
  characterLimit,
  bulletColor,
  inputBorderColor,
  ...props
}: IHighlightsInputProps) {
  const intl = useIntl();
  const addInputRef = createRef<HTMLInputElement>();
  const highlightRefs = value.map(() => createRef<HTMLInputElement>());

  return (
    <div
      css={{
        height: "100%",
        backgroundColor: "rgba(37, 192, 136, 0.05)",
        padding: 15,
      }}
      {...props}
    >
      <ul
        css={{
          listStyle: "none",
          margin: 0,
          padding: 0,
        }}
      >
        {(value as string[]).map((highlight, index) => (
          <li
            key={index}
            css={{
              display: "flex",
              alignItems: "center",
              "&:not(:first-of-type)": {
                marginTop: 10,
              },
            }}
          >
            <div css={{ marginRight: 10, display: "inline" }}>
              <div css={{ width: 13, textAlign: "center" }}>
                <HighlightBullet color={bulletColor} />
              </div>
            </div>

            <input
              ref={highlightRefs[index]}
              maxLength={characterLimit}
              placeholder={
                highlight === ""
                  ? intl.formatMessage({ defaultMessage: "Press Backspace to remove", id: "s48Fto" })
                  : intl.formatMessage({ defaultMessage: "Type another highlight...", id: "7P342p" })
              }
              css={{
                backgroundColor: palette.white,
                border: `solid 1px ${palette.lightestGray}`,
                fontSize: 14,
                height: 48,
                paddingLeft: 20,
                paddingRight: 20,
                letterSpacing: 0.5,
                color: palette.mediumGray,
                lineHeight: 1.14,
                width: "95%",
                outline: "none",
                borderRadius: 4,
                "&:focus": {
                  border: `solid 1px ${inputBorderColor || palette.greenSuccess}`,
                },
              }}
              value={highlight}
              onChange={(e) => {
                onChange(
                  (value as string[]).map((value, maybeIndex) => {
                    return index === maybeIndex ? e.target.value : value;
                  }),
                );
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  e.preventDefault();

                  if (e.currentTarget.value !== "" && addInputRef.current) {
                    addInputRef.current.focus();
                  }
                }

                if (e.key === "Backspace" && e.currentTarget.value === "") {
                  e.preventDefault();

                  const nextItems = (value as string[]).filter((_, maybeIndex) => {
                    return maybeIndex !== index;
                  });

                  onChange(nextItems);

                  if (nextItems.length === 0 && addInputRef.current) {
                    addInputRef.current.focus();
                  }

                  if (highlightRefs[index - 1] && highlightRefs[index - 1].current !== null) {
                    (highlightRefs[index - 1].current as HTMLInputElement).focus();
                  }
                }
              }}
            />

            <Margin ml={10}>
              <div css={{ width: 30, textAlign: "left" }}>
                <CharacterCount>{characterLimit - highlight.length}</CharacterCount>
              </div>
            </Margin>
          </li>
        ))}

        {value.length < maxItems && (
          <Value initial="">
            {({ value: highlight, set: setValue }) => (
              <li
                css={{
                  display: "flex",
                  alignItems: "center",
                  "&:not(:first-of-type)": {
                    marginTop: 10,
                  },
                }}
              >
                <div css={{ display: "inline", marginRight: 10 }}>
                  <div
                    css={{
                      width: 13,
                      height: 13,
                      textAlign: "center",
                      color: palette.sealBlue,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                    role="button"
                    aria-label="Add highlight"
                    onClick={(event) => {
                      event.preventDefault();

                      if (highlight.trim() !== "") {
                        setValue("");
                        onChange([...value, highlight.trim()]);
                      }

                      if (addInputRef.current) {
                        addInputRef.current.focus();
                      }
                    }}
                  >
                    <svg width={15} height={15}>
                      <path
                        d="M13.929 6.429H8.57V1.07a1.072 1.072 0 0 0-2.142 0V6.43H1.07a1.072 1.072 0 0 0 0 2.142H6.43v5.358a1.072 1.072 0 0 0 2.142 0V8.57h5.358a1.072 1.072 0 0 0 0-2.142z"
                        fill="currentColor"
                        fillRule="nonzero"
                      />
                    </svg>
                  </div>
                </div>

                <input
                  ref={addInputRef}
                  maxLength={characterLimit}
                  placeholder={intl.formatMessage({ defaultMessage: "Type another highlight...", id: "7P342p" })}
                  css={{
                    backgroundColor: palette.white,
                    border: `solid 1px ${palette.lightestGray}`,
                    fontSize: 14,
                    height: 48,
                    paddingLeft: 20,
                    paddingRight: 20,
                    letterSpacing: 0.5,
                    color: palette.mediumGray,
                    lineHeight: 1.14,
                    width: "95%",
                    outline: "none",
                    borderRadius: 4,
                    "&:focus": {
                      border: `solid 1px ${inputBorderColor || palette.greenSuccess}`,
                    },
                  }}
                  value={highlight}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();

                      if (highlight.trim() !== "") {
                        setValue("");
                        onChange([...value, highlight.trim()]);
                      }
                    } else if (e.key === "Backspace" && highlight.trim() === "" && highlightRefs.length > 1) {
                      (highlightRefs[highlightRefs.length - 1].current as HTMLInputElement).focus();
                    }
                  }}
                />

                <Margin ml={10}>
                  <div css={{ width: 30, textAlign: "left" }}>
                    <CharacterCount>{characterLimit - highlight.length}</CharacterCount>
                  </div>
                </Margin>
              </li>
            )}
          </Value>
        )}
      </ul>
    </div>
  );
}

function CharacterCount(props: any): ReactElement {
  return (
    <span
      css={{
        fontSize: 14,
        lineHeight: 1.71,
        color: palette.sealBlue,
        marginLeft: "auto",
      }}
      {...props}
    />
  );
}

const HighlightBullet: React.FunctionComponent<IHighlightBullet> = ({ color, ...props }) => {
  return (
    <span
      css={{
        backgroundColor: color || palette.lightGreen,
        width: 10,
        height: 10,
        display: "inline-block",
        borderRadius: "100%",
      }}
      {...props}
    />
  );
};
